/**
 * ZAXIA Voice Service
 * Phase D4.5.6: Azure TTS Integration
 *
 * ARCHITECTURE:
 * - Primary: Azure neural TTS via backend proxy (Ollie Dragon HD)
 * - Fallback: Browser speechSynthesis (only if Azure unavailable)
 *
 * Single source of truth for all concierge speech output.
 * All speak() calls are queued to prevent overlap.
 */

import { API_BASE } from '@/config/api';

// =============================================================================
// TYPES
// =============================================================================

export interface VoiceServiceOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
}

export interface SpeakOptions {
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

// Speech Recognition types (browser API)
export interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

export interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

export interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

export interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

export interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event & { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

// =============================================================================
// CONFIGURATION
// =============================================================================

// Fallback browser voice preferences (only used if Azure TTS fails)
const PREFERRED_VOICE_NAMES = [
  'Samantha',
  'Karen',
  'Google UK English Female',
  'Microsoft Zira',
];

const DEFAULT_OPTIONS: Required<VoiceServiceOptions> = {
  rate: 0.92,
  pitch: 1.06,
  volume: 1.0,
};

// =============================================================================
// VOICE SERVICE CLASS
// =============================================================================

interface QueueItem {
  text: string;
  options?: SpeakOptions;
}

/** Voice mode for demo cost savings */
export type VoiceMode = 'azure' | 'browser';

class ZaxiaVoiceService {
  private options: Required<VoiceServiceOptions>;
  private speechQueue: QueueItem[] = [];
  private isSpeakingNow = false;
  private neuralTtsAvailable: boolean | null = null;
  private fallbackVoice: SpeechSynthesisVoice | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private wasCancelled = false; // Track if current speech was cancelled
  private _voiceMode: VoiceMode = 'azure'; // Default to Azure, can be switched in demo mode

  constructor(options?: VoiceServiceOptions) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.checkNeuralTtsStatus();
    this.initFallbackVoice();
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Check if neural TTS (Azure) is available
   */
  private async checkNeuralTtsStatus(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/voice/status`);
      if (response.ok) {
        const status = await response.json();
        this.neuralTtsAvailable = status.configured;
        console.log('[ZaxiaVoice] Neural TTS status:', status);
      } else {
        this.neuralTtsAvailable = false;
        console.warn('[ZaxiaVoice] Neural TTS not available, using fallback');
      }
    } catch {
      this.neuralTtsAvailable = false;
      console.warn('[ZaxiaVoice] Could not check neural TTS status');
    }
  }

  /**
   * Initialize fallback browser voice
   */
  private initFallbackVoice(): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return;
    }

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        this.fallbackVoice = this.selectBestVoice(voices);
        console.log('[ZaxiaVoice] Fallback voice:', this.fallbackVoice?.name || 'none');
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }

  /**
   * Select the best available browser voice for fallback
   */
  private selectBestVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
    for (const name of PREFERRED_VOICE_NAMES) {
      const voice = voices.find(v => v.name.includes(name));
      if (voice) return voice;
    }
    return voices.find(v => v.lang.startsWith('en')) || null;
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Check if voice service is available
   */
  get isAvailable(): boolean {
    return this.neuralTtsAvailable === true ||
      (typeof window !== 'undefined' && 'speechSynthesis' in window);
  }

  /**
   * Check if currently speaking
   */
  get isSpeaking(): boolean {
    if (this.isSpeakingNow) return true;
    if (this.currentAudio && !this.currentAudio.paused) return true;
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      return window.speechSynthesis.speaking;
    }
    return false;
  }

  /**
   * Get current voice mode
   */
  get voiceMode(): VoiceMode {
    return this._voiceMode;
  }

  /**
   * Switch voice mode (demo mode only)
   * @param mode 'azure' for Azure TTS, 'browser' for free browser TTS
   */
  setVoiceMode(mode: VoiceMode): void {
    this._voiceMode = mode;
    console.log(`[ZaxiaVoice] Voice mode switched to: ${mode}`);
  }

  /**
   * Speak text - queued to prevent overlap
   * Uses neural TTS (Azure) as primary, browser TTS as fallback
   */
  speak(text: string, options?: SpeakOptions): void {
    console.log('[ZaxiaVoice] Queuing:', text.substring(0, 60) + (text.length > 60 ? '...' : ''));
    this.speechQueue.push({ text, options });
    this.processQueue();
  }

  /**
   * Cancel all speech (current and queued)
   */
  cancel(): void {
    this.speechQueue = [];
    this.isSpeakingNow = false;
    this.wasCancelled = true; // Mark as cancelled so in-flight promises don't trigger fallback

    // Stop audio element if playing.
    // Do NOT set src = '' — that fires an onerror event which pollutes the
    // console and can confuse the queue.  pause() + nulling the ref is enough.
    if (this.currentAudio) {
      const audio = this.currentAudio;
      this.currentAudio = null;
      // Remove event handlers so the promise from speakWithNeuralTts resolves
      // cleanly via wasCancelled check rather than through the error path.
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }

    // Cancel browser TTS if running
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  /**
   * Update voice options
   */
  setOptions(options: VoiceServiceOptions): void {
    this.options = { ...this.options, ...options };
  }

  // ===========================================================================
  // QUEUE PROCESSING
  // ===========================================================================

  /**
   * Process the speech queue
   */
  private processQueue(): void {
    if (this.isSpeakingNow || this.speechQueue.length === 0) {
      return;
    }

    const item = this.speechQueue.shift();
    if (!item) return;

    this.speakNow(item.text, item.options);
  }

  /**
   * Speak immediately (internal)
   */
  private async speakNow(text: string, options?: SpeakOptions): Promise<void> {
    this.isSpeakingNow = true;
    this.wasCancelled = false; // Reset cancellation flag

    // If voice mode is 'browser', skip Azure and use browser TTS directly
    if (this._voiceMode === 'browser') {
      console.log('[ZaxiaVoice] Using browser TTS (demo mode - cost savings)');
      this.speakWithBrowserTts(text, options);
      return;
    }

    // Try neural TTS first (Azure)
    if (this.neuralTtsAvailable) {
      const success = await this.speakWithNeuralTts(text, options);

      // If cancelled during the request, don't fall back - just stop
      if (this.wasCancelled) {
        return;
      }

      if (success) return;
      console.warn('[ZaxiaVoice] Neural TTS failed, falling back to browser');
    }

    // Fallback to browser TTS (only if not cancelled)
    if (!this.wasCancelled) {
      this.speakWithBrowserTts(text, options);
    }
  }

  // ===========================================================================
  // NEURAL TTS (Azure via backend)
  // ===========================================================================

  /**
   * Speak using Azure neural TTS
   */
  private async speakWithNeuralTts(text: string, options?: SpeakOptions): Promise<boolean> {
    try {
      console.log('[ZaxiaVoice] Fetching Azure TTS audio...');
      const response = await fetch(`${API_BASE}/voice/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('[ZaxiaVoice] Azure TTS response not OK:', response.status, error);
        // Only disable neural TTS if server explicitly says it's not configured
        // Don't disable on transient errors
        if (error.fallback && error.error === 'Neural TTS not configured') {
          this.neuralTtsAvailable = false;
        }
        return false;
      }

      // Create audio from response
      const audioBlob = await response.blob();
      console.log('[ZaxiaVoice] Audio blob received:', audioBlob.size, 'bytes, type:', audioBlob.type);

      if (audioBlob.size === 0) {
        console.error('[ZaxiaVoice] Empty audio blob received');
        return false;
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('[ZaxiaVoice] Audio URL created:', audioUrl);

      return new Promise<boolean>((resolve) => {
        const audio = new Audio(audioUrl);
        this.currentAudio = audio;
        audio.volume = this.options.volume;

        audio.onloadeddata = () => {
          console.log('[ZaxiaVoice] Audio data loaded, duration:', audio.duration);
        };

        audio.onplay = () => {
          console.log('[ZaxiaVoice] Neural TTS playing');
          options?.onStart?.();
        };

        audio.onended = () => {
          console.log('[ZaxiaVoice] Neural TTS ended');
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          options?.onEnd?.();
          this.finishSpeaking();
          resolve(true);
        };

        audio.onerror = (e) => {
          console.error('[ZaxiaVoice] Audio playback error:', e, audio.error);
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          resolve(false);
        };

        // Try to play - may fail due to autoplay policy
        audio.play().then(() => {
          console.log('[ZaxiaVoice] Audio play() started successfully');
        }).catch((err) => {
          console.error('[ZaxiaVoice] Audio play() failed:', err.name, err.message);
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          resolve(false);
        });
      });
    } catch (error) {
      console.error('[ZaxiaVoice] Neural TTS request failed:', error);
      return false;
    }
  }

  // ===========================================================================
  // BROWSER TTS (Fallback)
  // ===========================================================================

  /**
   * Speak using browser speechSynthesis (fallback only)
   */
  private speakWithBrowserTts(text: string, options?: SpeakOptions): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      console.warn('[ZaxiaVoice] Browser TTS not available');
      options?.onError?.(new Error('TTS not available'));
      this.finishSpeaking();
      return;
    }

    // Chrome bug workaround
    window.speechSynthesis.resume();

    // Add natural pauses
    const processedText = this.addNaturalPauses(text);

    const utterance = new SpeechSynthesisUtterance(processedText);
    utterance.rate = this.options.rate;
    utterance.pitch = this.options.pitch;
    utterance.volume = this.options.volume;

    if (this.fallbackVoice) {
      utterance.voice = this.fallbackVoice;
    }

    let started = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    utterance.onstart = () => {
      started = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      console.log('[ZaxiaVoice] Browser TTS started (fallback)');
      options?.onStart?.();
    };

    utterance.onend = () => {
      console.log('[ZaxiaVoice] Browser TTS ended');
      options?.onEnd?.();
      this.finishSpeaking();
    };

    utterance.onerror = () => {
      options?.onEnd?.();
      this.finishSpeaking();
    };

    window.speechSynthesis.speak(utterance);

    // Fallback if speech doesn't start
    fallbackTimer = setTimeout(() => {
      if (!started) {
        console.warn('[ZaxiaVoice] Browser TTS failed to start');
        options?.onStart?.();
        options?.onEnd?.();
        this.finishSpeaking();
      }
    }, 1000);
  }

  /**
   * Add natural pauses for browser TTS
   */
  private addNaturalPauses(text: string): string {
    return text
      .replace(/\. /g, '... ')
      .replace(/! /g, '!... ')
      .replace(/\? /g, '?... ')
      .replace(/, /g, ',.. ');
  }

  /**
   * Clean up after speaking
   */
  private finishSpeaking(): void {
    this.isSpeakingNow = false;
    setTimeout(() => this.processQueue(), 50);
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const zaxiaVoice = new ZaxiaVoiceService();

// =============================================================================
// SPEECH RECOGNITION HELPERS
// =============================================================================

/**
 * Check if Speech Recognition is available
 */
export function isSpeechRecognitionAvailable(): boolean {
  return typeof window !== 'undefined' &&
    (!!window.SpeechRecognition || !!window.webkitSpeechRecognition);
}

/**
 * Create a Speech Recognition instance
 */
export function createSpeechRecognition(): SpeechRecognition | null {
  if (!isSpeechRecognitionAvailable()) return null;

  const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognitionAPI();

  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'en-US';

  return recognition;
}

export default ZaxiaVoiceService;
